// Procedural surface textures for map geometry.
//
// A `MapSurface` names a texture family; this module draws that family once
// into an offscreen canvas and hands back a cached three.js material. Nothing
// is fetched, so surfaces cost no network request, no CSP allowance and no
// asset pipeline - a map document is still pure data.
//
// Caching has two levels:
//   * one canvas (and one GPU upload) per texture family, and
//   * one cloned Texture per (family, repeat) pair - `Texture.clone()` shares
//     the underlying `source`, so clones do not re-upload the image.
// Materials are then cached per (family, tint, finish, repeat), which lets the
// seventy-odd walls of a floor share a handful of materials.

import {
  CanvasTexture,
  Color,
  LinearMipmapLinearFilter,
  MeshStandardMaterial,
  RepeatWrapping,
  SRGBColorSpace,
  type Texture,
} from 'three';

import type { MapSurface, MapSurfaceTexture } from '../content/schema/map';

/** Edge length of every generated texture, in pixels. */
const TEXTURE_SIZE = 256;

/** Repeat values are snapped to this step so near-identical walls share a texture. */
const REPEAT_SNAP = 0.25;

/** Deterministic PRNG so a family always draws identically across reloads. */
function makeRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function createCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  return { canvas, ctx };
}

/** Monochrome speckle laid over the whole tile; the base of most families. */
function speckle(ctx: CanvasRenderingContext2D, seed: number, amount: number, size = 1) {
  const random = makeRandom(seed);
  for (let i = 0; i < TEXTURE_SIZE * TEXTURE_SIZE * amount; i += 1) {
    const x = random() * TEXTURE_SIZE;
    const y = random() * TEXTURE_SIZE;
    const shade = 200 + Math.floor(random() * 55);
    ctx.fillStyle = `rgba(${shade},${shade},${shade},${0.10 + random() * 0.22})`;
    ctx.fillRect(x, y, size, size);
  }
}

/** Soft dark blotches - grime, damp, wear. Drawn last so they read on top. */
function blotches(ctx: CanvasRenderingContext2D, seed: number, count: number, maxRadius: number) {
  const random = makeRandom(seed);
  for (let i = 0; i < count; i += 1) {
    const x = random() * TEXTURE_SIZE;
    const y = random() * TEXTURE_SIZE;
    const radius = maxRadius * (0.35 + random() * 0.65);
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    const darkness = 0.06 + random() * 0.13;
    gradient.addColorStop(0, `rgba(0,0,0,${darkness})`);
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }
}

/**
 * Draw a family into a fresh canvas. Everything is drawn in near-white greys so
 * the material's `color` acts as a tint: a family is a *pattern*, the document
 * chooses the colour.
 */
function drawFamily(family: MapSurfaceTexture): HTMLCanvasElement | null {
  const made = createCanvas();
  if (!made) return null;
  const { canvas, ctx } = made;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);

  switch (family) {
    case 'plaster': {
      speckle(ctx, 11, 0.35, 2);
      blotches(ctx, 12, 14, 46);
      break;
    }

    case 'wallpaper': {
      // Vertical stripes with a faint repeating motif between them.
      const stripe = TEXTURE_SIZE / 8;
      for (let i = 0; i < 8; i += 1) {
        ctx.fillStyle = i % 2 === 0 ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)';
        ctx.fillRect(i * stripe, 0, stripe, TEXTURE_SIZE);
      }
      const random = makeRandom(21);
      ctx.strokeStyle = 'rgba(0,0,0,0.07)';
      ctx.lineWidth = 1.5;
      for (let row = 0; row < 6; row += 1) {
        for (let col = 0; col < 6; col += 1) {
          const cx = (col + 0.5) * (TEXTURE_SIZE / 6);
          const cy = (row + 0.5) * (TEXTURE_SIZE / 6);
          ctx.beginPath();
          ctx.ellipse(cx, cy, 7, 11, random() * Math.PI, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      blotches(ctx, 22, 10, 52);
      break;
    }

    case 'panelling': {
      // Tongue-and-groove boards running vertically, with a shadowed groove.
      const board = TEXTURE_SIZE / 6;
      for (let i = 0; i < 6; i += 1) {
        const x = i * board;
        ctx.fillStyle = i % 2 === 0 ? 'rgba(0,0,0,0.035)' : 'rgba(255,255,255,0.03)';
        ctx.fillRect(x, 0, board, TEXTURE_SIZE);
        ctx.fillStyle = 'rgba(0,0,0,0.28)';
        ctx.fillRect(x, 0, 2, TEXTURE_SIZE);
        ctx.fillStyle = 'rgba(255,255,255,0.16)';
        ctx.fillRect(x + 2, 0, 1, TEXTURE_SIZE);
      }
      speckle(ctx, 31, 0.16, 1);
      break;
    }

    case 'brick': {
      const rows = 8;
      const brickH = TEXTURE_SIZE / rows;
      const brickW = TEXTURE_SIZE / 4;
      const random = makeRandom(41);
      ctx.fillStyle = 'rgba(0,0,0,0.16)';
      ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
      for (let row = 0; row < rows; row += 1) {
        const offset = row % 2 === 0 ? 0 : brickW / 2;
        for (let col = -1; col < 5; col += 1) {
          const x = col * brickW + offset;
          const y = row * brickH;
          const shade = 226 + Math.floor(random() * 29);
          ctx.fillStyle = `rgb(${shade},${shade},${shade})`;
          ctx.fillRect(x + 1.5, y + 1.5, brickW - 3, brickH - 3);
        }
      }
      blotches(ctx, 42, 16, 40);
      break;
    }

    case 'concrete': {
      speckle(ctx, 51, 0.55, 2);
      blotches(ctx, 52, 20, 58);
      // Shuttering lines.
      ctx.strokeStyle = 'rgba(0,0,0,0.12)';
      ctx.lineWidth = 2;
      for (const y of [TEXTURE_SIZE / 3, (TEXTURE_SIZE * 2) / 3]) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(TEXTURE_SIZE, y);
        ctx.stroke();
      }
      break;
    }

    case 'tile': {
      const cells = 4;
      const cell = TEXTURE_SIZE / cells;
      ctx.fillStyle = 'rgba(0,0,0,0.22)';
      ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
      const random = makeRandom(61);
      for (let row = 0; row < cells; row += 1) {
        for (let col = 0; col < cells; col += 1) {
          const shade = 238 + Math.floor(random() * 17);
          ctx.fillStyle = `rgb(${shade},${shade},${shade})`;
          ctx.fillRect(col * cell + 2, row * cell + 2, cell - 4, cell - 4);
        }
      }
      blotches(ctx, 62, 8, 34);
      break;
    }

    case 'floorboard': {
      const boards = 5;
      const boardH = TEXTURE_SIZE / boards;
      const random = makeRandom(71);
      for (let i = 0; i < boards; i += 1) {
        const y = i * boardH;
        const shade = 232 + Math.floor(random() * 23);
        ctx.fillStyle = `rgb(${shade},${shade},${shade})`;
        ctx.fillRect(0, y, TEXTURE_SIZE, boardH);
        // Grain.
        ctx.strokeStyle = 'rgba(0,0,0,0.07)';
        ctx.lineWidth = 1;
        for (let g = 0; g < 7; g += 1) {
          const gy = y + random() * boardH;
          ctx.beginPath();
          ctx.moveTo(0, gy);
          ctx.bezierCurveTo(
            TEXTURE_SIZE * 0.3, gy + (random() - 0.5) * 4,
            TEXTURE_SIZE * 0.7, gy + (random() - 0.5) * 4,
            TEXTURE_SIZE, gy,
          );
          ctx.stroke();
        }
        // Board gap.
        ctx.fillStyle = 'rgba(0,0,0,0.34)';
        ctx.fillRect(0, y, TEXTURE_SIZE, 2);
        // Butt joint.
        const jointX = random() * TEXTURE_SIZE;
        ctx.fillRect(jointX, y, 2, boardH);
      }
      blotches(ctx, 72, 10, 44);
      break;
    }

    case 'carpet': {
      speckle(ctx, 81, 1.5, 1);
      speckle(ctx, 82, 0.9, 2);
      blotches(ctx, 83, 12, 50);
      break;
    }

    case 'lino': {
      const cells = 8;
      const cell = TEXTURE_SIZE / cells;
      for (let row = 0; row < cells; row += 1) {
        for (let col = 0; col < cells; col += 1) {
          if ((row + col) % 2 !== 0) continue;
          ctx.fillStyle = 'rgba(0,0,0,0.055)';
          ctx.fillRect(col * cell, row * cell, cell, cell);
        }
      }
      speckle(ctx, 91, 0.25, 1);
      blotches(ctx, 92, 9, 40);
      break;
    }

    case 'stone': {
      const random = makeRandom(101);
      ctx.fillStyle = 'rgba(0,0,0,0.2)';
      ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
      // Irregular courses of rubble stone.
      let y = 0;
      while (y < TEXTURE_SIZE) {
        const courseH = TEXTURE_SIZE / 6 + random() * 12;
        let x = -random() * 30;
        while (x < TEXTURE_SIZE) {
          const stoneW = TEXTURE_SIZE / 5 + random() * 34;
          const shade = 216 + Math.floor(random() * 39);
          ctx.fillStyle = `rgb(${shade},${shade},${shade})`;
          ctx.fillRect(x + 2, y + 2, stoneW - 4, courseH - 4);
          x += stoneW;
        }
        y += courseH;
      }
      blotches(ctx, 102, 18, 46);
      break;
    }

    case 'metal': {
      const random = makeRandom(111);
      // Brushed streaks.
      for (let i = 0; i < 260; i += 1) {
        const y = random() * TEXTURE_SIZE;
        const shade = 214 + Math.floor(random() * 41);
        ctx.strokeStyle = `rgba(${shade},${shade},${shade},0.4)`;
        ctx.lineWidth = random() * 1.6;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(TEXTURE_SIZE, y + (random() - 0.5) * 2);
        ctx.stroke();
      }
      // Rivets at the corners of a plate.
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      const rivets: Array<[number, number]> = [
        [14, 14],
        [TEXTURE_SIZE - 14, 14],
        [14, TEXTURE_SIZE - 14],
        [TEXTURE_SIZE - 14, TEXTURE_SIZE - 14],
      ];
      for (const [x, y] of rivets) {
        ctx.beginPath();
        ctx.arc(x, y, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
      blotches(ctx, 112, 10, 38);
      break;
    }

    case 'none':
    default:
      return canvas;
  }

  return canvas;
}

// ── Relief ────────────────────────────────────────────────────────────────────
//
// A family's canvas is drawn as a greyscale pattern before it is tinted, which
// makes its luminance a serviceable height field. Two more maps are derived
// from it once per family:
//
//   * a normal map, so a torch grazing a wall picks out the courses of a brick
//     wall and the boards of a floor rather than just making a lighter patch,
//     and
//   * a roughness map, so grout reads matte against glazed tile and the gaps
//     between floorboards do not shine like the boards do.
//
// Both are derived rather than authored, so a family only ever has to be drawn
// once and the two stay in step with it. Neither is sRGB - they are data, not
// colour - so they are left at three's default `NoColorSpace`.

/**
 * Per-family relief. `normal` scales the derived normal map, `roughness` is how
 * far the roughness map is allowed to fall below the material's own value.
 *
 * The numbers are the physical story of each family: rubble stone and brick are
 * deeply modelled, plaster is almost flat, lino is flat and slightly shiny, and
 * tile is smooth with rough grout between.
 */
const RELIEF: Record<MapSurfaceTexture, { normal: number; roughness: number }> = {
  none: { normal: 0, roughness: 0 },
  plaster: { normal: 0.35, roughness: 0.16 },
  wallpaper: { normal: 0.3, roughness: 0.14 },
  panelling: { normal: 0.6, roughness: 0.2 },
  brick: { normal: 1.0, roughness: 0.3 },
  concrete: { normal: 0.7, roughness: 0.24 },
  tile: { normal: 0.5, roughness: 0.55 },
  floorboard: { normal: 0.7, roughness: 0.3 },
  carpet: { normal: 0.5, roughness: 0.1 },
  lino: { normal: 0.25, roughness: 0.35 },
  stone: { normal: 1.1, roughness: 0.26 },
  metal: { normal: 0.3, roughness: 0.5 },
};

/** Luminance of the canvas, as a wrapping height field in [0, 1]. */
function heightField(canvas: HTMLCanvasElement): Float32Array | null {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const { data } = ctx.getImageData(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
  const height = new Float32Array(TEXTURE_SIZE * TEXTURE_SIZE);
  for (let i = 0; i < height.length; i += 1) {
    const o = i * 4;
    // Rec. 601 luma, which is close enough for a height proxy and cheap.
    height[i] = (data[o]! * 0.299 + data[o + 1]! * 0.587 + data[o + 2]! * 0.114) / 255;
  }
  return height;
}

/**
 * A tangent-space normal map from a height field, by central difference.
 *
 * Sampling wraps, because the tile it came from wraps: reading past an edge
 * with clamped indices leaves a seam down every repeat of every wall.
 */
function normalCanvas(height: Float32Array, strength: number): HTMLCanvasElement | null {
  const made = createCanvas();
  if (!made) return null;
  const { canvas, ctx } = made;
  const image = ctx.createImageData(TEXTURE_SIZE, TEXTURE_SIZE);
  const at = (x: number, y: number) =>
    height[((y + TEXTURE_SIZE) % TEXTURE_SIZE) * TEXTURE_SIZE + ((x + TEXTURE_SIZE) % TEXTURE_SIZE)]!;

  for (let y = 0; y < TEXTURE_SIZE; y += 1) {
    for (let x = 0; x < TEXTURE_SIZE; x += 1) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength * TEXTURE_SIZE * 0.02;
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength * TEXTURE_SIZE * 0.02;
      // Normalise (-dx, -dy, 1) into the 0-255 range three expects.
      const length = Math.hypot(dx, dy, 1);
      const o = (y * TEXTURE_SIZE + x) * 4;
      image.data[o] = Math.round(((-dx / length) * 0.5 + 0.5) * 255);
      image.data[o + 1] = Math.round(((-dy / length) * 0.5 + 0.5) * 255);
      image.data[o + 2] = Math.round((1 / length) * 0.5 * 255 + 127.5);
      image.data[o + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  return canvas;
}

/**
 * A roughness map from the same height field: recesses are rough, raised faces
 * are smoother. Written into the green channel three reads, and kept in
 * `[1 - amount, 1]` so it modulates the material's own roughness rather than
 * replacing it - a map centred on mid-grey would halve every surface's
 * roughness the moment one was attached.
 */
function roughnessCanvas(height: Float32Array, amount: number): HTMLCanvasElement | null {
  const made = createCanvas();
  if (!made) return null;
  const { canvas, ctx } = made;
  const image = ctx.createImageData(TEXTURE_SIZE, TEXTURE_SIZE);
  for (let i = 0; i < height.length; i += 1) {
    const value = Math.round((1 - amount * height[i]!) * 255);
    const o = i * 4;
    image.data[o] = value;
    image.data[o + 1] = value;
    image.data[o + 2] = value;
    image.data[o + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
  return canvas;
}

const canvasCache = new Map<MapSurfaceTexture, HTMLCanvasElement | null>();
const baseTextureCache = new Map<MapSurfaceTexture, CanvasTexture | null>();
const reliefTextureCache = new Map<string, CanvasTexture | null>();
const repeatTextureCache = new Map<string, Texture>();
const materialCache = new Map<string, MeshStandardMaterial>();

function baseTexture(family: MapSurfaceTexture): CanvasTexture | null {
  if (family === 'none') return null;
  if (baseTextureCache.has(family)) return baseTextureCache.get(family) ?? null;

  let canvas = canvasCache.get(family);
  if (canvas === undefined) {
    canvas = drawFamily(family);
    canvasCache.set(family, canvas);
  }
  if (!canvas) {
    baseTextureCache.set(family, null);
    return null;
  }

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  baseTextureCache.set(family, texture);
  return texture;
}

/** The derived normal or roughness texture for a family, drawn once. */
function reliefTexture(family: MapSurfaceTexture, kind: 'normal' | 'roughness'): CanvasTexture | null {
  if (family === 'none') return null;
  const key = `${family}:${kind}`;
  if (reliefTextureCache.has(key)) return reliefTextureCache.get(key) ?? null;

  const relief = RELIEF[family];
  const strength = kind === 'normal' ? relief.normal : relief.roughness;
  let source = canvasCache.get(family);
  if (source === undefined) {
    source = drawFamily(family);
    canvasCache.set(family, source);
  }
  const height = source && strength > 0 ? heightField(source) : null;
  const canvas = height
    ? kind === 'normal'
      ? normalCanvas(height, strength)
      : roughnessCanvas(height, strength)
    : null;
  if (!canvas) {
    reliefTextureCache.set(key, null);
    return null;
  }

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  reliefTextureCache.set(key, texture);
  return texture;
}

function snap(value: number): number {
  return Math.max(REPEAT_SNAP, Math.round(value / REPEAT_SNAP) * REPEAT_SNAP);
}

function repeatedTexture(
  family: MapSurfaceTexture,
  repeatX: number,
  repeatY: number,
  kind: 'map' | 'normal' | 'roughness' = 'map',
): Texture | null {
  const base = kind === 'map' ? baseTexture(family) : reliefTexture(family, kind);
  if (!base) return null;
  const rx = snap(repeatX);
  const ry = snap(repeatY);
  const key = `${family}:${kind}:${rx}:${ry}`;
  const cached = repeatTextureCache.get(key);
  if (cached) return cached;

  // `clone()` shares `source`, so every repeat variant of a family reuses the
  // single GPU upload of its canvas.
  const texture = base.clone();
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(rx, ry);
  texture.needsUpdate = true;
  repeatTextureCache.set(key, texture);
  return texture;
}

/** A darker shade of a colour, for generated trim. */
export function shadeColor(color: string, amount: number): string {
  const c = new Color(color);
  c.multiplyScalar(amount);
  return `#${c.getHexString()}`;
}

export interface SurfaceMaterialRequest {
  surface?: MapSurface;
  /** Tint used when the surface names none (the owner's `color`). */
  fallbackColor: string;
  /** World extents of the surface in metres, used to derive texture repeats. */
  worldSize: [number, number];
}

/**
 * Resolve a `MapSurface` into a shared `MeshStandardMaterial`. Materials are
 * cached and reused; callers must not mutate or dispose the result.
 */
export function surfaceMaterial({
  surface,
  fallbackColor,
  worldSize,
}: SurfaceMaterialRequest): MeshStandardMaterial {
  const family: MapSurfaceTexture = surface?.texture ?? 'none';
  const color = surface?.color ?? fallbackColor;
  const scale = surface?.scale ?? 1;
  const roughness = surface?.roughness ?? 0.9;
  const metalness = surface?.metalness ?? 0.04;
  const emissive = surface?.emissive ?? 0;

  const repeatX = snap(Math.abs(worldSize[0]) * scale);
  const repeatY = snap(Math.abs(worldSize[1]) * scale);
  const key = `${family}:${color}:${roughness}:${metalness}:${emissive}:${repeatX}:${repeatY}`;
  const cached = materialCache.get(key);
  if (cached) return cached;

  const texture = repeatedTexture(family, repeatX, repeatY);
  // Relief derived from the same tile, so a family only has to be drawn once
  // and its shape, its shine and its colour cannot drift apart.
  const normalMap = repeatedTexture(family, repeatX, repeatY, 'normal');
  const roughnessMap = repeatedTexture(family, repeatX, repeatY, 'roughness');
  const material = new MeshStandardMaterial({
    color,
    roughness,
    metalness,
    map: texture ?? null,
    normalMap: normalMap ?? null,
    roughnessMap: roughnessMap ?? null,
    emissive: emissive > 0 ? new Color(color) : new Color('#000000'),
    emissiveIntensity: emissive,
  });
  if (normalMap) material.normalScale.set(1, 1);
  materialCache.set(key, material);
  return material;
}

/** Drop every cached canvas, texture and material. Test/HMR hygiene only. */
export function clearSurfaceCaches(): void {
  for (const material of materialCache.values()) material.dispose();
  for (const texture of repeatTextureCache.values()) texture.dispose();
  for (const texture of baseTextureCache.values()) texture?.dispose();
  for (const texture of reliefTextureCache.values()) texture?.dispose();
  materialCache.clear();
  repeatTextureCache.clear();
  baseTextureCache.clear();
  reliefTextureCache.clear();
  canvasCache.clear();
}
